/**
 * Serialized in ReviewView via `saveResults()` → localStorage `courtyard_review_results`.
 * Injected into Ask / Agent / Editor model calls as fixed party-identification context.
 */
export const REVIEW_RESULTS_STORAGE_KEY = 'courtyard_review_results';

/** Full review UI state including findings (ReviewView). */
export const REVIEW_COMPLETE_STATE_STORAGE_KEY = 'courtyard_review_complete_state';

export type ReviewResultsStored = {
  ourSide?: string[];
  theirSide?: string[];
  optionalDetails?: string;
  hasRedlines?: boolean;
  sideAssignments?: Record<string, 'our_side' | 'their_side'>;
};

function ourSideNonEmpty(data: ReviewResultsStored | null | undefined): boolean {
  return ((data?.ourSide ?? []) as string[]).filter(Boolean).length > 0;
}

/**
 * True when the user has finished the Review party-identification flow (at least one "our side" entity saved).
 */
export function hasReviewPartyContextComplete(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const rawResults = localStorage.getItem(REVIEW_RESULTS_STORAGE_KEY);
    if (rawResults) {
      const data = JSON.parse(rawResults) as ReviewResultsStored;
      if (ourSideNonEmpty(data)) return true;
    }
    const rawComplete = localStorage.getItem(REVIEW_COMPLETE_STATE_STORAGE_KEY);
    if (rawComplete) {
      const state = JSON.parse(rawComplete) as ReviewResultsStored;
      if (ourSideNonEmpty(state)) return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * Returns a concise instruction block for the model, or undefined if the user has not completed Review sides.
 */
export function getReviewPartyContextInstruction(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = localStorage.getItem(REVIEW_RESULTS_STORAGE_KEY);
    if (!raw) return undefined;
    const data = JSON.parse(raw) as ReviewResultsStored;
    const our = (data.ourSide || []).filter(Boolean);
    if (our.length === 0) return undefined;
    const their = (data.theirSide || []).filter(Boolean);
    const lines: string[] = [
      'Party identification (from the Word add-in Review flow):',
      `- The user's party ("our side" / "my side"): ${our.join(', ')}`,
      `- The other side: ${their.length ? their.join(', ') : '(not specified)'}`,
    ];
    if (data.optionalDetails?.trim()) {
      lines.push(`- User notes: ${data.optionalDetails.trim()}`);
    }
    if (data.hasRedlines && data.sideAssignments && Object.keys(data.sideAssignments).length > 0) {
      const bits = Object.entries(data.sideAssignments).map(
        ([author, side]) =>
          `${author} → ${side === 'our_side' ? "the user's party" : 'the other side'}`
      );
      lines.push(`- Track changes authors mapped to sides: ${bits.join('; ')}`);
    }
    lines.push(
      'When the user refers to "my party", "our side", "favor us", or similar, use the identification above.'
    );
    return lines.join('\n');
  } catch {
    return undefined;
  }
}
